<script lang="ts">
  import { PUBLIC_ENABLE_MAINTENANCE_MODE } from "$env/static/public";
  import { slide } from "svelte/transition";
  import {
    menuIsOpen,
    theme,
    pageMainColor,
    pageMainColorUsage,
    headerVariant,
    headerHidden,
    hideHeader,
  } from "$stores";
  import { browser } from "$app/environment";
  import { ButtonColor } from "$types/atoms";
  import Button from "$lib/atoms/Button.svelte";
  import { onMount, getContext } from "svelte";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import gsap from "gsap";
  import BurgerButton from "$lib/atoms/BurgerButton.svelte";
  import MenuDrawer from "$lib/molecules/MenuDrawer.svelte";
  import { beforeNavigate } from "$app/navigation";
  import optimizeImage, { aspectRatio } from "$lib/utils/optimizeImage";
  import { type ThemeBackgroundColor } from "$lib/utils/helpers";
  import { page } from "$app/stores";
  import TextCard from "$lib/elements/TextCard.svelte";
  import type { SubnavStoryblok } from "$types/components";
  import type { Link } from "$types/data";

  // Check if this is a Consumer or Business page
  $: isBusinessPage = $page.url.pathname.startsWith("/business");
  $: isConsumerPage = !isBusinessPage;

  // Check if this is the Check Availability page
  $: isCheckAvailabilityPage = $page.url.pathname.startsWith(
    "/check-availability",
  );

  // Check if this is the Maintenance page
  $: isMaintenancePage =
    PUBLIC_ENABLE_MAINTENANCE_MODE === "true" && $page.url.pathname == "/";

  export let header: {
    subnav?: SubnavStoryblok[];
    link: Link;
    heading: string;
  }[];
  export let offsetTop: number;
  export let background: ThemeBackgroundColor;
  export let enableAnimation = false;

  // number of sub nav items above which we render a smaller visual size
  const SMALL_SUBNAV_THRESHOLD = 10;

  let wrapper: HTMLElement;
  let scroll = 0;

  onMount(() => {
    gsap.registerPlugin(ScrollTrigger);

    // Only initialize the scrolltrigger if header animation is enabled
    if (enableAnimation === true) {
      const scroller = new ScrollTrigger({
        trigger: wrapper,
        start: "top top",
        end: 99999,
        onUpdate: (self) => {
          const { direction } = self;
          scroll = self.scroll();

          if (direction === -1) {
            gsap.to(wrapper, {
              duration: 0.8,
              autoAlpha: 1,
              y: 0,
              ease: "power3.out",
            });
            $headerHidden = false;
          } else if (direction == 1 && !$menuIsOpen && scroll > 100) {
            gsap.to(wrapper, {
              duration: 0.8,
              autoAlpha: 1,
              y: `-100%+${offsetTop > 0 ? offsetTop + 10 : 0}px`,
              ease: "power3.out",
            });
            $headerHidden = true;
          } else {
            gsap.to(wrapper, {
              duration: 0.8,
              autoAlpha: 1,
              y: 0,
              ease: "power3.out",
            });
            $headerHidden = false;
          }
        },
      });

      return () => {
        scroller && scroller.kill();
      };
    }
  });

  function toggleDrawer() {
    $menuIsOpen = !$menuIsOpen;
  }

  let activeSubnav: SubnavStoryblok | undefined;

  beforeNavigate(() => {
    activeSubnav = undefined;
    $menuIsOpen = false;
  });

  let isScrolled = false;
  function onScroll() {
    if (enableAnimation) {
      activeSubnav = undefined;
      isScrolled = window.scrollY > 0;
    }
  }

  function lightOrDark(
    lightValue: string | ButtonColor,
    darkValue: string | ButtonColor,
    // The values passed to the _dependencies array aren't used here. Instead,
    // they affect when this function is called.
    _dependencies: unknown[],
  ) {
    const hasColorfulBackground =
      $pageMainColorUsage === "hero-footer-full-bleed" ||
      $pageMainColorUsage === "page";
    const backgroundIsTransparent =
      $headerVariant === "transparent-light" ||
      $headerVariant === "transparent-dark";
    const backgroundIsDark =
      $pageMainColor === "green" || $pageMainColor === "blue";
    const hasNotScrolled = !isScrolled;
    const hasNoActiveSubnav = !activeSubnav;
    const menuIsClosed = !$menuIsOpen;

    if (
      hasColorfulBackground &&
      backgroundIsDark &&
      hasNotScrolled &&
      hasNoActiveSubnav &&
      menuIsClosed
    ) {
      return lightValue;
    } else if (backgroundIsTransparent && hasNoActiveSubnav && menuIsClosed) {
      if (scroll < 100) {
        return lightValue;
      } else {
        return darkValue;
      }
    } else {
      return darkValue;
    }
  }
</script>

<svelte:window on:scroll={onScroll} />

<header
  class={`
    flex
    ${$theme}
    inset-x-0
    z-50
    ${activeSubnav ? "!bg-white with-subnav" : ""}
    main-color-${$pageMainColor || "none"}
    main-color-usage-${$pageMainColorUsage}
    ${isScrolled || activeSubnav ? "is-scrolled" : ""}
    transition-colors
    duration-300
    ${enableAnimation ? "animation-enabled" : "animation-disabled"}
    ${isMaintenancePage ? "is-maintenance-page" : ""}
  `}
  class:bg-sand={($pageMainColorUsage === "hero-footer" &&
    background === "sand" &&
    !$headerVariant.includes("transparent")) ||
    ($headerVariant.includes("transparent") && scroll > 100)}
  class:bg-transparent-light={$headerVariant === "transparent-light" &&
    scroll < 100}
  class:bg-transparent-dark={$headerVariant === "transparent-dark" &&
    scroll < 100}
  class:menuOpen={$menuIsOpen}
  bind:this={wrapper}
  style={`top: ${offsetTop}px`}
  class:hidden={$hideHeader}
>
  <div class="relative w-full h-full flex items-center">
    <div class="section-header-width w-full h-full mx-auto">
      <div class="-ml-4 flex flex-auto h-full">
        <!-- logo -->
        <div
          class={`${
            isConsumerPage ? "w-2/12" : "w-3/12"
          } pl-4 flex flex-col justify-center`}
        >
          <a
            href={`${isConsumerPage ? "/" : "/business"}`}
            class="logo"
            aria-label="Home"
          >
            <svg
              class="!h-auto w-full logo-mark"
              viewBox="0 0 77 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g
                clip-path="url(#clip0_707_5001)"
                fill={lightOrDark("#fff", "#242021", [
                  $pageMainColorUsage,
                  $pageMainColor,
                  $menuIsOpen,
                  isScrolled,
                  activeSubnav,
                  $headerVariant,
                  scroll,
                ])}
              >
                <path
                  d="M0.490372 18.5208C-0.412456 19.0033 0.0302658 20.5327 1.02349 20.8593L13.6399 24.9922C13.6758 25.0035 13.7141 25.0024 13.75 24.9888L27.034 19.6075C27.1684 19.5529 27.1615 19.364 27.0236 19.3185L14.9646 15.3643C13.881 15.0104 13.4301 13.4947 14.734 12.8028L26.6423 6.47942C27.5451 5.99694 27.1024 4.46758 26.1091 4.141L13.4916 0.00809421C13.4556 -0.00328495 13.4174 -0.00214703 13.3815 0.011508L0.0986443 5.39271C-0.0357947 5.44733 -0.028841 5.63622 0.109075 5.68174L12.168 9.636C13.2517 9.98989 13.7025 11.5056 12.3987 12.1974L0.490372 18.5208Z"
                />
                <path
                  d="M34.3863 15.9194L40.1614 8.9531H34.5462V7.51364H42.1061V9.08055L36.4179 16.0184H42.4689V17.4863H34.3863V15.9194Z"
                />
                <path d="M43.556 10.363H45.2677V17.4863H43.556V10.363Z" />
                <path
                  d="M46.6458 10.363H48.3436L48.3147 11.9447H48.4016C48.5754 11.2176 49.4029 10.1491 50.9119 10.1491C52.7836 10.1491 54.1477 11.6591 54.1477 13.8814C54.1477 16.1038 52.8566 17.6991 50.9698 17.6991C49.3739 17.6991 48.6334 16.6022 48.4155 15.9331H48.3425V20.0501H46.6446V10.3619L46.6458 10.363ZM50.3312 16.2893C51.4774 16.2893 52.3919 15.4916 52.3919 13.9099C52.3919 12.3282 51.4925 11.5453 50.3312 11.5453C49.17 11.5453 48.2996 12.343 48.2996 13.9099C48.2996 15.4768 49.17 16.2893 50.3312 16.2893Z"
                />
                <path
                  d="M55.2209 15.5052V7.51251H56.9037V15.2196C56.9037 15.7749 57.1216 16.089 57.7022 16.089H58.167V17.4852H57.2525C55.9174 17.4852 55.2209 16.8298 55.2209 15.5052Z"
                />
                <path
                  d="M58.9504 7.51366H60.6622V9.19436H58.9504V7.51366ZM58.9504 10.363H60.6622V17.4863H58.9504V10.363Z"
                />
                <path
                  d="M62.0552 10.363H63.7531L63.7241 12.1871H63.7971C64 11.0901 64.9723 10.1502 66.2344 10.1502C67.9752 10.1502 68.7447 11.2187 68.7447 13.1702V17.4863H67.033V13.383C67.033 12.1723 66.4813 11.5305 65.495 11.5305C64.4068 11.5305 63.7543 12.6274 63.7543 13.7528V17.4852H62.0564V10.3619L62.0552 10.363Z"
                />
                <path
                  d="M69.6719 13.9247C69.6719 11.6022 71.1959 10.1491 73.3864 10.1491C75.5768 10.1491 76.9988 11.3883 76.9988 13.7255C76.9988 13.9816 76.9849 14.167 76.956 14.3093H71.3118C71.3698 15.6907 72.2552 16.403 73.4153 16.403C74.3008 16.403 74.9973 16.0036 75.2291 15.1639H76.898C76.6222 16.8446 75.1862 17.6991 73.3284 17.6991C71.3257 17.6991 69.6719 16.403 69.6719 13.9235V13.9247ZM75.2732 13.2977C75.2732 12.1575 74.5187 11.3746 73.3435 11.3746C72.1683 11.3746 71.4277 12.1723 71.3559 13.2977H75.2732Z"
                />
                <path d="M45.2677 7.51366H43.556V9.19436H45.2677V7.51366Z" />
              </g>
              <defs>
                <clipPath id="clip0_707_5001">
                  <rect width="77" height="25" fill="white" />
                </clipPath>
              </defs>
            </svg>

            {#if isBusinessPage}
              <span class="logo-text">Business</span>
            {/if}
          </a>
        </div>

        <!-- menu items -->
        <div class="w-10/12 pl-4 h-full">
          <div
            class={`
              main-navigation
              h-full
              w-full
              flex
              items-center
              justify-end
              lg:justify-between
            `}
          >
            <ul class="menu h-full hidden lg:flex">
              {#each header as pageItem}
                <li
                  class="menu-item h-full flex flex-col justify-center"
                  on:mouseenter={() =>
                    (pageItem?.subnav?.length ?? 0) > 0
                      ? (activeSubnav = pageItem.subnav?.[0])
                      : null}
                  on:mouseleave={() => (activeSubnav = undefined)}
                >
                  <a
                    href={(pageItem?.subnav?.length ?? 0) > 0
                      ? "#"
                      : "/" + pageItem.link.cached_url}
                    class="type:body-small {$page.url.pathname ===
                    '/' + (pageItem.link.cached_url || '#')
                      ? 'is-active'
                      : ''}"
                  >
                    {pageItem.heading}
                  </a>

                  {#if activeSubnav && activeSubnav._uid === pageItem.subnav?.[0]?._uid && activeSubnav.items}
                    <div
                      transition:slide|global={{ duration: 300 }}
                      class="absolute top-[100%] inset-x-0 z-10"
                    >
                      <div
                        class="bg-white py-[38px] lg:py-[45px] w-full"
                        style="border-top: 0.5px solid rgba(0, 0, 0, 0.10);"
                      >
                        <div class="section-header-width w-full mx-auto">
                          <div class="flex -ml-4">
                            <div
                              class="pl-4 {activeSubnav.items.length === 3
                                ? 'w-[22%]'
                                : 'w-[30%]'}"
                            >
                              <div class="pt-4">
                                {#if activeSubnav.textCard?.[0]}
                                  <TextCard blok={activeSubnav.textCard[0]} />
                                {/if}
                              </div>
                            </div>
                            <div
                              class="pl-4 grid {activeSubnav.items.length === 3
                                ? 'w-[78%] grid-cols-3'
                                : 'w-[70%] grid-cols-2'} gap-1"
                            >
                              {#each activeSubnav.items as item}
                                <a
                                  href={"/" + (item.link?.cached_url ?? "")}
                                  class="p-[1rem] {activeSubnav.items.length <=
                                  SMALL_SUBNAV_THRESHOLD
                                    ? 'lg:p-[27px_27px_25px_24px] min-h-[137px]'
                                    : ''} rounded-[3px] hover:bg-[#F2F1EF] flex transition-colors duration-200 text-inherit no-underline"
                                >
                                  <div class="flex-[0_0_30px] w-[30px]">
                                    {#if item.icon}
                                      <img
                                        src={optimizeImage(item.icon.filename)}
                                        alt={item.icon.alt}
                                        class="w-full"
                                        style={aspectRatio(item.icon.filename)}
                                      />
                                    {/if}
                                  </div>
                                  <div
                                    class="pl-[20px] flex-auto flex flex-col"
                                  >
                                    <div
                                      class="{activeSubnav.items.length <=
                                      SMALL_SUBNAV_THRESHOLD
                                        ? 'text-[16px] mb-2'
                                        : 'text-[14px] mb-1'}  font-bold text-charcoal"
                                    >
                                      {@html item.heading}
                                    </div>
                                    <div
                                      class="text-[14px] {activeSubnav.items
                                        .length <= SMALL_SUBNAV_THRESHOLD
                                        ? 'lg:text-[16px]'
                                        : ''} font-light text-[#838383] flex-auto"
                                      style={activeSubnav.items.length <=
                                      SMALL_SUBNAV_THRESHOLD
                                        ? "max-width:250px"
                                        : ""}
                                    >
                                      {item.content}
                                    </div>
                                  </div>
                                </a>
                              {/each}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  {/if}
                </li>
              {/each}
            </ul>
            <div class="contact-cta gap-2 hidden lg:flex lg:pl-[1rem]">
              {#if isConsumerPage && !isCheckAvailabilityPage}
                <Button
                  href="/check-availability"
                  color={lightOrDark(ButtonColor.WHITE, ButtonColor.YELLOW, [
                    $pageMainColorUsage,
                    $pageMainColor,
                    $menuIsOpen,
                    isScrolled,
                    activeSubnav,
                    $headerVariant,
                    scroll,
                  ])}
                  reduced
                  on:click={() => {
                    // Trigger the GTM click event
                    if (browser && window.dataLayer) {
                      window.dataLayer.push({
                        event: "get_delivery_button_click",
                      });
                    }
                  }}>Get delivery</Button
                >
              {/if}

              {#if isBusinessPage}
                <Button
                  href="/business/contact"
                  color={lightOrDark(ButtonColor.WHITE, ButtonColor.BLUE, [
                    $pageMainColorUsage,
                    $pageMainColor,
                    $menuIsOpen,
                    isScrolled,
                    activeSubnav,
                    $headerVariant,
                    scroll,
                  ])}
                  reduced>Contact sales</Button
                >
              {/if}
            </div>

            <BurgerButton
              menuOpen={$menuIsOpen}
              color={lightOrDark("white", "black", [
                $pageMainColorUsage,
                $pageMainColor,
                $menuIsOpen,
                isScrolled,
                activeSubnav,
                $headerVariant,
                scroll,
              ])}
              on:click={toggleDrawer}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</header>

<!-- {#if $menuIsOpen === true} -->
<MenuDrawer pages={header} {offsetTop} />

<!-- {/if} -->
<style lang="scss">
  header {
    align-items: center;
    height: var(--main-top-padding);
    top: 0;
    left: 0;
    width: 100%;
    --nav-text-color: rgb(var(--color-text-dark));

    &.animation-enabled {
      position: fixed;
      backdrop-filter: blur(16px);
      transform: translate3d(0, 0, 0);
      transition: background-color 0.2s;
    }

    &.animation-disabled {
      position: absolute !important;
      transform: none !important;
      background-color: none !important;
    }

    &.bg-sand {
      background: rgba(230, 228, 217, 0.65);
    }
    &.bg-transparent-light {
      &:not(.menuOpen) {
        background: transparent;
        backdrop-filter: none;
        &:not(.with-subnav) {
          --nav-text-color: var(--color-white);
        }
      }
      &.menuOpen {
        background-color: rgb(var(--color-light-grey));
      }
    }
    &.bg-transparent-dark {
      --nav-text-color: var(--color-text-dark);
      &.menuOpen {
        background-color: rgb(var(--color-light-grey));
      }
    }
    &:not([class^="bg-"], [class*=" bg-"]) {
      background: rgb(var(--color-light-grey));
    }

    &:not(.is-scrolled):not(.menuOpen) {
      &.main-color-usage-hero-footer-full-bleed,
      &.main-color-usage-page {
        background: none;
        backdrop-filter: none;
        &.main-color-green,
        &.main-color-blue {
          &,
          * {
            color: #fff !important;
            border-color: #fff !important;
          }
          .menu-item {
            a {
              &:after {
                background: rgb(var(--color-white));
              }
            }
          }
        }
      }
    }
  }

  :global(.burger-menu) {
    position: relative;
    left: 6px;
  }

  .logo {
    display: flex;
    align-items: center;
    text-decoration: none;
  }

  .logo .logo-mark {
    width: 78px;
    min-width: 78px;
    display: block;
  }

  .logo .logo-text {
    color: rgb(var(--color-text-dark));
    font-size: 16px;
    font-weight: 700;
    margin-left: 8px;
    padding-left: 8px;
    border-left: 1px solid #b5b2b1;
  }

  .menu-item {
    padding-right: 30px;
    &:last-of-type {
      padding-right: 0;
    }
    > a {
      text-decoration: none;
      color: rgb(var(--nav-text-color));
      position: relative;

      &:after {
        content: "";
        position: absolute;
        bottom: em(-6px, 14px);
        left: 0;
        height: 2px;
        width: 100%;
        background: rgb(var(--nav-text-color));
        opacity: 0;
        transition: opacity 0.2s;
      }

      &:hover:after {
        opacity: 1;
      }
    }
  }

  .with-subnav .menu-item > a:hover:after {
    background: rgb(var(--color-text-dark));
  }
</style>
